import {
  SimpleRoleType,
  WorkspaceSimpleRoleType,
} from "src/clients/taktile-api";
import { useRoleOverride } from "src/hooks/useCapabilities/RoleOverrideStore";
import { isOrgRoleAtLeast } from "src/hooks/useCapabilities/roleChecks";
import {
  ANY_FOLDER,
  Capabilities,
  FlowFolderCapability,
  FlowFolderScope,
  JobScope,
} from "src/hooks/useCapabilities/types";
import { useIsTaktileOrgUser } from "src/hooks/useIsTaktileOrgUser";
import { DashboardPageParams } from "src/router/urls";
import {
  useHasFwdRoleForOrg,
  useIsFWDOrAdmin,
  useIsTaktileAdmin,
  useUserRoles,
} from "src/store/AuthStore";
import { useSimpleRole } from "src/store/SimpleRoleProvider";
import {
  ALL_GRANULAR_ROLES,
  FOLDER_DEPENDENCY_KEY,
  FolderGranularRole,
  GranularRoleKey,
  getGranularRoleDependencyKey,
  getImpliedGranularRoles,
  NO_FOLDER_ARGUMENT,
  WILDCARD_ARGUMENT,
  WORKSPACE_ADMIN_ROLE,
  WORKSPACE_MEMBER_ROLE,
} from "src/userManagement/userPermissions/permissionFeatures";
import { TokenRole } from "src/utils/decodeTokenScope";
import { useParamsDecode } from "src/utils/useParamsDecode";

type WorkspaceScope = {
  orgId: string | undefined;
  wsId: string | undefined;
};

const matchesArgument = (value: string | undefined, expected: string) =>
  value === expected || value === WILDCARD_ARGUMENT;

const isHeldInWorkspace = (role: TokenRole, { orgId, wsId }: WorkspaceScope) =>
  !!orgId &&
  !!wsId &&
  matchesArgument(role.arguments.org_id, orgId) &&
  matchesArgument(role.arguments.ws_id, wsId);

const isGranularRole = (roleName: string) =>
  !!getGranularRoleDependencyKey(roleName);

const isFolderGranularRole = (roleName: string) =>
  getGranularRoleDependencyKey(roleName) === FOLDER_DEPENDENCY_KEY;

export type GranularRoleGrants = {
  /** Workspace administration has no granular role of its own; ws_manager carries it. */
  isWorkspaceAdmin: boolean;
  /** ws_member on its own confers no capability, but it does open the workspace. */
  isWorkspaceMember: boolean;
  /** Expanded through the roles each held role contains, so one lookup answers. */
  workspaceRoles: Set<GranularRoleKey>;
  /** Flow roles are granted per folder, keyed by the folder argument of the grant. */
  folderRoles: Map<FolderGranularRole, Set<string>>;
};

/** Grants held across the whole workspace, so every folder is covered. */
const workspaceWideGrants = (roleNames: string[]): GranularRoleGrants => {
  const granularRoles = roleNames.filter(isGranularRole) as GranularRoleKey[];

  return {
    isWorkspaceAdmin: false,
    isWorkspaceMember: false,
    workspaceRoles: new Set(
      granularRoles.filter((role) => !isFolderGranularRole(role)),
    ),
    folderRoles: new Map(
      granularRoles
        .filter(isFolderGranularRole)
        .map((role) => [
          role as FolderGranularRole,
          new Set([WILDCARD_ARGUMENT]),
        ]),
    ),
  };
};

export const ALL_GRANULAR_ROLE_GRANTS: GranularRoleGrants = {
  ...workspaceWideGrants(ALL_GRANULAR_ROLES),
  isWorkspaceAdmin: true,
  isWorkspaceMember: true,
};

export const collectGranularRoleGrants = (
  roles: TokenRole[] | undefined,
  scope: WorkspaceScope,
): GranularRoleGrants => {
  const heldRoles = (roles ?? []).filter((role) =>
    isHeldInWorkspace(role, scope),
  );

  const workspaceRoles = new Set(
    heldRoles
      .filter(
        (role) => isGranularRole(role.name) && !isFolderGranularRole(role.name),
      )
      .flatMap((role) => [
        role.name as GranularRoleKey,
        ...getImpliedGranularRoles(role.name),
      ]),
  );

  const isWorkspaceAdmin = heldRoles.some(
    (role) => role.name === WORKSPACE_ADMIN_ROLE,
  );
  // ws_manager contains ws_member.
  const isWorkspaceMember =
    isWorkspaceAdmin ||
    heldRoles.some((role) => role.name === WORKSPACE_MEMBER_ROLE);

  const folderRoles = [
    ...heldRoles
      .filter(
        (role) =>
          isFolderGranularRole(role.name) && !!role.arguments.flow_folder_id,
      )
      .flatMap((role) =>
        [
          role.name as FolderGranularRole,
          ...(getImpliedGranularRoles(role.name) as FolderGranularRole[]),
        ].map(
          (granularRole) =>
            [granularRole, role.arguments.flow_folder_id] as const,
        ),
      ),
    // ws_manager reads flows in every folder, per its definition in roles.yaml.
    ...(isWorkspaceAdmin
      ? ([["flow_viewer", WILDCARD_ARGUMENT]] as const)
      : []),
  ].reduce<GranularRoleGrants["folderRoles"]>(
    (grants, [granularRole, folder]) =>
      grants.set(
        granularRole,
        new Set([...(grants.get(granularRole) ?? []), folder]),
      ),
    new Map(),
  );

  return {
    isWorkspaceAdmin,
    isWorkspaceMember,
    workspaceRoles,
    folderRoles,
  };
};

const hasFolderRole = (
  folderRoles: GranularRoleGrants["folderRoles"],
  granularRole: FolderGranularRole,
  folder: FlowFolderScope,
): boolean => {
  const grantedFolders = folderRoles.get(granularRole);
  if (!grantedFolders) {
    return false;
  }
  if (folder === ANY_FOLDER) {
    return grantedFolders.size > 0;
  }
  if (grantedFolders.has(WILDCARD_ARGUMENT)) {
    return true;
  }

  return grantedFolders.has(folder ?? NO_FOLDER_ARGUMENT);
};

type GranularCapabilitiesInput = {
  grants: GranularRoleGrants;
  orgRole: Nullable<SimpleRoleType>;
  isFWDOrAdmin: boolean;
  isFWDOrAdminForOrg: boolean;
  isLoading: boolean;
};

export const buildGranularCapabilities = ({
  grants,
  orgRole,
  isFWDOrAdmin,
  isFWDOrAdminForOrg,
  isLoading,
}: GranularCapabilitiesInput): Capabilities => {
  const has = (granularRole: GranularRoleKey) =>
    grants.workspaceRoles.has(granularRole);

  const canViewLogic = (folder: FlowFolderScope) =>
    hasFolderRole(grants.folderRoles, "flow_viewer", folder);
  const canEditLogic = (folder: FlowFolderScope) =>
    hasFolderRole(grants.folderRoles, "flow_editor", folder);
  const canDeployLogic = (folder: FlowFolderScope) =>
    hasFolderRole(grants.folderRoles, "flow_deployer", folder);
  const canUseSandboxLogic = (folder: FlowFolderScope) =>
    hasFolderRole(grants.folderRoles, "flow_execute_sandbox", folder);
  // Acting on an active Job touches live traffic, so it takes deploy access.
  const canManageJob = ({ folder, isActive }: JobScope) =>
    isActive ? canDeployLogic(folder) : canEditLogic(folder);

  const canViewAgents = has("agent_viewer");

  const canViewConnections = has("connection_viewer");

  // flow_viewer carries sandbox decision history, per its definition in roles.yaml.
  const canReadSandboxHistory: FlowFolderCapability = (folder) =>
    has("dh_viewer_sandbox") || canViewLogic(folder);
  const canReadLiveHistory: FlowFolderCapability = () => has("dh_viewer_live");
  const canViewHistory: FlowFolderCapability = (folder) =>
    canReadSandboxHistory(folder) || canReadLiveHistory(folder);

  const canViewDataAndCases = has("data_case_viewer");
  const canEditDataAndCases = has("data_case_editor");
  const canAdminDataAndCases = has("data_case_admin");

  const { isWorkspaceAdmin, isWorkspaceMember } = grants;
  const isOrgAdmin = isOrgRoleAtLeast(SimpleRoleType.ADMIN, orgRole);
  const holdsAnyRoleInWorkspace =
    isWorkspaceMember ||
    grants.workspaceRoles.size > 0 ||
    grants.folderRoles.size > 0;

  return {
    role: orgRole,
    workspaceRole: isWorkspaceAdmin
      ? WorkspaceSimpleRoleType.ADMIN
      : holdsAnyRoleInWorkspace
        ? "ws_member"
        : null,
    isLoading,
    flowVersions: {
      canAccess: canViewLogic,
      canPublish: (flow) => canDeployLogic(flow.flow_folder_id),
      canArchive: canDeployLogic,
      canSetSandboxDefault: canEditLogic,
      canSetDefault: canDeployLogic,
      canCreate: canEditLogic,
      canEdit: canEditLogic,
      canDeleteDraft: canEditLogic,
    },
    datasets: {
      canAccess: canViewLogic,
      canDelete: canEditLogic,
    },
    decisionHistory: {
      canAccess: canViewHistory,
      canReadLive: canReadLiveHistory,
      canReadSandbox: canReadSandboxHistory,
    },
    flows: {
      canAccess: canViewLogic,
      canCreate: canEditLogic,
      canEdit: canEditLogic,
      canDeleteUnpublished: canEditLogic,
      canDeletePublished: canDeployLogic,
    },
    apiKeys: {
      canAccess: isWorkspaceAdmin,
    },
    aiUsageLimits: {
      canAccess: isWorkspaceAdmin,
    },
    connections: {
      canAccess: canViewConnections,
    },
    usersPermissions: {
      canAccess: isOrgAdmin || isWorkspaceAdmin,
      canFilterByWorkspace: isOrgAdmin,
    },
    workspaces: {
      canAccess: holdsAnyRoleInWorkspace,
      canCreate: isOrgAdmin,
      canEdit: isWorkspaceAdmin,
    },
    outgoingWebhooks: {
      canAccess: isWorkspaceAdmin,
    },
    manualReview: {
      canAccess: canViewDataAndCases,
    },
    flowCharts: {
      canAccess: canEditLogic,
      canCreate: canEditLogic,
      canEdit: canEditLogic,
      canDelete: canEditLogic,
    },
    jobs: {
      canAccess: canViewLogic,
      canCreate: canEditLogic,
      canEdit: canManageJob,
      canDelete: canManageJob,
      canDeleteSource: canManageJob,
      canDeleteDestination: canManageJob,
      canToggleStatus: canDeployLogic,
      canRunSandbox: canUseSandboxLogic,
      canRunLive: canDeployLogic,
      canChangeSchedule: canManageJob,
      canDuplicate: canEditLogic,
    },
    flowFolders: {
      canCreate: isWorkspaceAdmin,
      canEdit: isWorkspaceAdmin,
      canDelete: isWorkspaceAdmin,
      canMoveFlows: isWorkspaceAdmin,
    },
    auditLogs: {
      canAccess: isOrgAdmin,
    },
    outcomes: {
      canAccess: isWorkspaceAdmin,
      canCreate: isWorkspaceAdmin,
      canEdit: isWorkspaceAdmin,
      canDelete: isWorkspaceAdmin,
      canClear: isWorkspaceAdmin,
    },
    entities: {
      canAccess: canViewDataAndCases,
    },
    featureCatalogue: {
      canAccess: canViewDataAndCases,
      canCreate: canAdminDataAndCases || isFWDOrAdmin,
      canEdit: canAdminDataAndCases || isFWDOrAdmin,
      canDuplicate: canAdminDataAndCases || isFWDOrAdmin,
    },
    featureQueriesCatalogue: {
      canAccess: canViewDataAndCases,
      canCreate: canEditDataAndCases,
      canEdit: canEditDataAndCases,
      canDuplicate: canEditDataAndCases,
      canActivate: canEditDataAndCases,
      // Backfill is hidden in the UI while the action is broken (ATL-1494).
      canBackfill: false,
      canViewQueryDetails: canViewDataAndCases,
      canCreateQuery: canEditDataAndCases,
      canEditSandboxQuery: canEditDataAndCases,
      canEditLiveQuery: canAdminDataAndCases || isFWDOrAdmin,
      canPreviewLive: canEditDataAndCases,
      canPreviewSandbox: canEditDataAndCases,
      canChangeSandboxStatus: canEditDataAndCases,
      canChangeLiveStatus: canAdminDataAndCases,
      canDeleteDisabledQuery: canEditDataAndCases,
      canArchive: canEditDataAndCases,
    },
    organization: {
      canEditLogo: isOrgAdmin,
    },
    sensitiveFields: {
      canReveal: isWorkspaceAdmin,
      canEdit: isWorkspaceAdmin,
    },
    apiDocs: {
      canAccess: isWorkspaceAdmin,
    },
    caseTemplates: {
      canAccess: canViewDataAndCases,
      canEdit: canEditDataAndCases,
      canCreateVersion: canAdminDataAndCases,
      canCreate: isFWDOrAdminForOrg || isOrgAdmin,
      canArchivePublished: canAdminDataAndCases,
    },
    caseQueues: {
      canEdit: canAdminDataAndCases,
      canCreate: canAdminDataAndCases,
    },
    cases: {
      canAccess: canViewDataAndCases,
      canCreate: canViewDataAndCases,
      canManage: canAdminDataAndCases,
      canAssign: canEditDataAndCases,
      canViewDecisionTrace: canEditDataAndCases,
      canEditBlocks: canViewDataAndCases,
    },
    caseAnalytics: {
      canAccess: canEditDataAndCases,
    },
    caseAnalyticsDashboards: {
      canCreate: canAdminDataAndCases,
    },
    liveAnalytics: {
      canAccess: isWorkspaceAdmin,
    },
    // No granular role carries workspace_analytics_dashboard; only ws_admin
    // confers it in the granular cohort.
    workspaceInsights: {
      canAccess: isWorkspaceAdmin,
      canEdit: isWorkspaceAdmin,
    },
    caseFiles: {
      canEdit: canViewDataAndCases,
    },
    simulations: {
      canAccess: isWorkspaceAdmin,
    },
    notebooks: {
      canAccess: isWorkspaceAdmin,
    },
    caseHistory: {
      canSeeDetails: isWorkspaceAdmin,
    },
    agents: {
      canAccess: canViewAgents,
      canEdit: has("agent_editor"),
      canDeploy: has("agent_deployer"),
      canRunSandbox: has("agent_execute_sandbox"),
      canRunLive: has("agent_execute_live"),
    },
    prebuiltAgents: {
      canAccessSystemPrompt: isFWDOrAdminForOrg,
      canWrite: isFWDOrAdminForOrg,
    },
    scimToken: {
      canAccess: isOrgAdmin,
    },
  };
};

export const useGranularCapabilities = (): Capabilities => {
  const { role, isLoading } = useSimpleRole();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const roles = useUserRoles();
  const actualIsFWDOrAdmin = useIsFWDOrAdmin();
  const isTaktileAdmin = useIsTaktileAdmin();
  const hasFwdForOrg = useHasFwdRoleForOrg(orgId);
  const isTaktileUser = useIsTaktileOrgUser();
  const { overrideWorkspaceRole, overrideFWDOrAdmin } = useRoleOverride();

  const isOverridden = isTaktileUser && overrideWorkspaceRole !== null;
  const actualIsFWDOrAdminForOrg = isTaktileAdmin || hasFwdForOrg;

  // Taktile staff reach the workspace through their own roles; an override
  // drops that, so it can never widen what it sees.
  const grants =
    actualIsFWDOrAdminForOrg && !isOverridden
      ? ALL_GRANULAR_ROLE_GRANTS
      : collectGranularRoleGrants(roles, { orgId, wsId });

  return buildGranularCapabilities({
    grants,
    orgRole: role,
    isFWDOrAdmin: isOverridden ? overrideFWDOrAdmin : actualIsFWDOrAdmin,
    isFWDOrAdminForOrg: isOverridden
      ? overrideFWDOrAdmin
      : actualIsFWDOrAdminForOrg,
    isLoading,
  });
};
